import React from 'react';
import { ICON_TINTS } from './lib/colors';
import { ICON_PATHS } from '../shared/MetricCardShell';
import { clickableRowProps } from '../shared/tableStyles';

/**
 * One summary figure: an icon, its label and its value, inside an outlined tile.
 *
 * ── Why the outline ─────────────────────────────────────────────────────────────
 * A tinted icon square beside bare text left the two reading as separate things — the
 * icon looked like a bullet the label had drifted away from, and four of them in a row
 * gave the eye no edges to stop at. The outline draws the boundary the row already
 * implies, and takes the icon's own tone so tile and glyph read as one object.
 *
 * The border is the tone at 30–40% rather than the icon square's 10%: a fill that faint
 * disappears entirely as a one-pixel line.
 *
 * ── One component, three panels ─────────────────────────────────────────────────
 * `indexing_health`, `sitemap_health` and `data_coverage` all draw this tile. They had
 * three near-identical private copies, changed in lockstep three times running — the
 * fourth change is where one of them silently gets missed. This is the single definition.
 *
 * ── What the colour does NOT mean ───────────────────────────────────────────────
 * The tone marks WHICH figure this is; it never judges the number. Every hue in
 * `ICON_TINTS` is absent from the rest of the page for that reason — see the note there.
 * §12 bans alert styling here, so no tile may read as an alarm.
 *
 * Renders nothing without a value: a tile is a fact, and an outlined box around a blank
 * is a fact that failed to load.
 *
 * @param {string} label
 * @param {string} value pre-formatted display string — this component computes nothing (§8)
 * @param {string} icon a key of `ICON_PATHS`
 * @param {'cyan'|'violet'|'fuchsia'|'pink'|'teal'|'rose'|'stone'} tone
 * @param {boolean} [big] render the value at headline size, for a single lead figure
 * @param {string} [description] one plain-language line under the value saying what the
 *        figure means
 */
const StatTile = ({ label, value, icon, tone, big = false, description, onActivate, activateLabel }) => {
  if (!value) return null;
  const tint = ICON_TINTS[tone] ?? ICON_TINTS.cyan;
  // A tile that opens something is keyboard-operable, but stays a <div> so its <dt>/<dd>
  // remain valid children of the surrounding <dl>.
  const interactive = typeof onActivate === 'function'
    ? {
      ...clickableRowProps(onActivate),
      role: 'button',
      'aria-label': activateLabel || label,
    }
    : {};
  const interactiveClass = typeof onActivate === 'function'
    ? 'cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]'
    : '';

  return (
    <div
      {...interactive}
      className={`flex items-start gap-3.5 rounded-xl border ${tint.border} ${tint.panel} p-4 ${interactiveClass}`}
    >
      {/* `h-12` matches the label + value stack, so the square reads as the tile's full
          height rather than a badge floating beside taller text. Fixed rather than
          stretched: a label that wraps on a narrow card would otherwise inflate it. */}
      <span className={`h-12 w-12 shrink-0 rounded-lg flex items-center justify-center ${tint.bg} ${tint.fg}`}>
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          // The label beside it already says what this is; an icon announced as well
          // would be the label read twice.
          aria-hidden="true"
        >
          {ICON_PATHS[icon] ?? ICON_PATHS.target}
        </svg>
      </span>
      <span className="min-w-0">
        {/* Small uppercase caption over a big number — the same KPI anatomy the headline
            strip uses (`MetricTile`), so the whole page's stat tiles read as one family. */}
        <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-tight">{label}</dt>
        <dd className={`mt-0.5 text-gray-900 dark:text-gray-100 font-bold tabular-nums leading-none ${big ? 'text-3xl' : 'text-2xl'}`}>
          {value}
        </dd>
        {description && (
          <dd className="mt-1.5 text-[11px] leading-snug text-gray-400 dark:text-gray-500">{description}</dd>
        )}
      </span>
    </div>
  );
};

export default StatTile;
